<template>
  <USlideover :ui="{ header: 'sm:py-6', body: 'px-0 sm:px-0 py-0 sm:py-0' }">
    <template #header>
      <div class="flex items-center justify-between w-full">
        <div class="font-semibold uppercase text-black text-2xl">{{ $t('DjCartLayer.cart') }}</div>
        <div class="flex items-center gap-6">
          <UButton
            variant="outline"
            :ui="{
              base: 'rounded-sm text-sm font-semibold text-black border-black border-2 ring-0 p-4 h-[42px] hover:bg-black/5',
            }"
            :label="$t('DjCartLayer.goToCart')"
            @click="goToCart"
          />
          <UButton
            variant="ghost"
            icon="heroicons:arrow-right"
            class="sm:hidden text-black"
            @click="hideCart"
          />
        </div>
      </div>
    </template>
    <template #body>
      <div class="flex flex-col h-full">
        <div
          v-if="!isEmpty"
          ref="scrollContainer"
          class="flex-1 overflow-y-auto space-y-6 max-h-[calc(100vh-315px)] py-6"
          @scroll="onScroll"
        >
          <div v-for="(cartLine, index) in cartProducts" :key="getLineKey(cartLine, index)">
            <DjCartMiniProducts :cart-line="cartLine" @refresh="refresh" />
          </div>
          <div v-if="isLoadingMore" class="flex justify-center py-2">
            <UButton loading variant="ghost" color="neutral" />
          </div>
          <div ref="loadMoreSentinel" class="h-1" />
        </div>
        <div
          v-else
          class="flex-1 text-xl text-black flex flex-col gap-2 items-center justify-center"
        >
          <Icon name="djust-icon:cart" size="64" />
          {{ $t('DjCartLayer.empty') }}
        </div>

        <div :key="increment" class="px-6 bg-white sticky bottom-0">
          <!-- Franco -->
          <div class="pt-6">
            <div class="py-1.5 flex gap-1.5">
              <Icon name="djust-icon:information" />
              <div class="flex flex-col gap-2 text-[#706F6F]">
                <div class="text-base leading-[100%]">
                  {{ $t('DjCartSummary.franco', { franco: formattedFranco }) }}
                </div>
                <div class="text-sm leading-[100%]">
                  {{ $t('DjCartSummary.deliveryFee', { fee: formattedDeliveryFee }) }}
                </div>
              </div>
            </div>
          </div>
          <div class="py-6">
            <!-- Totals -->
            <div
              class="mb-4 py-2 flex flex-col gap-4 text-[var(--color-black-950)] font-semibold text-sm"
            >
              <div class="flex justify-between">
                <span class="leading-[100%]">
                  {{ $t('DjCartLayer.totalHt') }}
                </span>
                <span class="text-lg leading-[100%]">
                  {{ getFormattedPrice(totalHTVA, currency) }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="leading-[100%]">{{ $t('DjCartLayer.shippingFees') }}</span>
                <span class="leading-[100%]">
                  {{ getFormattedPrice(currentDeliveryFee, currency) }}
                </span>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex flex-col gap-4">
              <UButton
                variant="solid"
                class="flex-1 items-center justify-center mr-0 py-2.5"
                :ui="{ base: 'rounded-sm bg-black ring-black hover:bg-black/75' }"
                :label="$t('DjCartLayer.order')"
                @click="goToCart"
              />
              <UButton
                variant="outline"
                class="flex-1 items-center justify-center py-2.5"
                :ui="{ base: 'rounded-sm text-black ring-black hover:bg-black/5' }"
                :label="$t('DjCartLayer.emptyCart')"
                @click="emptyCart"
              />
            </div>
          </div>
        </div>
      </div>
    </template>
  </USlideover>
</template>

<script setup lang="ts">
  import dayjs from 'dayjs';
  const router = useRouter();
  const { t } = useI18n();
  const { getFormattedPrice } = useUtils();
  const { getUserCurrency } = useUser();
  const toast = useToast();
  const userStore = useUserStore();
  const { getCustomFieldValueById } = useAccount();
  const { clearCart, getCartLines } = useCart();
  const currency = computed(() => getUserCurrency());
  const emit = defineEmits(['deleted', 'error']);
  const cartStore = useCartStore();
  const cart = computed(() => cartStore.cart);
  const cartLines = computed(() => cartStore.cartLines);
  const localePath = useLocalePath();
  const increment = ref(0);
  const account = computed(() => userStore.account);
  const overlay = useOverlay();
  const defaultPaginationSize = Number(useRuntimeConfig().public.defaultPaginationSize);
  const isLoadingMore = ref(false);
  const scrollContainer = ref<HTMLElement | null>(null);
  const loadMoreSentinel = ref<HTMLElement | null>(null);
  let intersectionObserver: IntersectionObserver | null = null;

  const totalLines = computed(() => cartStore.cartLinesTotal);
  // Stops the observer from looping if a page comes back empty while the total says otherwise
  const reachedEnd = ref(false);
  const hasMore = computed(() => !reachedEnd.value && cartLines.value.length < totalLines.value);

  function hideCart() {
    overlay.overlays[0].isOpen = false;
  }

  function goToCart() {
    router.push(localePath(`/checkout/cart`));
  }

  const getLineKey = (cartLine: CartLine, index: number) =>
    cartLine.offer?.offerPriceId || cartLine.id || index;

  const cartProducts = computed(() => {
    return [...cartLines.value].sort((a: CartLine, b: CartLine) =>
      dayjs(a.createdAt).isBefore(dayjs(b.createdAt)) ? 1 : -1
    );
  });

  const isEmpty = computed(() => totalLines.value === 0);
  const totalHTVA = computed(() => {
    return cart.value.totalPrice.priceWithoutTax;
  });

  const franco = computed(() => getCustomFieldValueById(account.value, 'FRANCO_PORT'));
  const deliveryFee = computed(() => getCustomFieldValueById(account.value, 'FRAIS_PORT'));
  const francoReached = computed(() => totalHTVA.value >= franco.value);
  const formattedFranco = computed(() => getFormattedPrice(franco.value, currency.value));
  const formattedDeliveryFee = computed(() => getFormattedPrice(deliveryFee.value, currency.value));
  const currentDeliveryFee = computed(() =>
    francoReached.value || isEmpty.value ? 0 : deliveryFee.value
  );

  function refresh() {
    increment.value++;
  }

  const loadMore = async () => {
    if (isLoadingMore.value || !hasMore.value || !cart.value?.id) return;

    isLoadingMore.value = true;
    try {
      const nextPage = (cartStore.cartLinesPage ?? 0) + 1;
      const loadedCount = cartLines.value.length;
      await getCartLines(
        {
          cartId: cart.value.id,
          currency: currency.value as Currency,
          pageable: { page: nextPage, size: defaultPaginationSize },
          append: true,
        },
        true
      );
      if (cartLines.value.length === loadedCount) reachedEnd.value = true;
    } catch (error) {
      console.error('Failed to load more cart lines:', error);
    } finally {
      isLoadingMore.value = false;
    }
  };

  const onScroll = () => {
    const el = scrollContainer.value;
    if (!el || isLoadingMore.value || !hasMore.value) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distanceFromBottom < 80) {
      loadMore();
    }
  };

  onMounted(() => {
    setupObserver();
  });

  watch(totalLines, () => {
    reachedEnd.value = false;
  });

  watch(isEmpty, async (empty: boolean) => {
    if (!empty) {
      await nextTick();
      setupObserver();
    }
  });

  const setupObserver = () => {
    intersectionObserver?.disconnect();
    if (!loadMoreSentinel.value) return;

    intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadMore();
        }
      },
      { root: scrollContainer.value, rootMargin: '80px' }
    );
    intersectionObserver.observe(loadMoreSentinel.value);
  };

  onBeforeUnmount(() => {
    intersectionObserver?.disconnect();
  });

  async function emptyCart() {
    try {
      await clearCart(cart.value.id);
      toast.add({
        title: t('DjCartLayer.success.clear'),
        color: 'success',
      });
    } catch (e) {
      toast.add({
        title: t('DjCartLayer.error.clear'),
        color: 'error',
      });
    }
  }
</script>
